import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import StatsStrip from "@/components/board/StatsStrip";
import StageSpine from "@/components/board/StageSpine";
import KanbanColumn from "@/components/board/KanbanColumn";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import NewLeadDialog from "@/components/board/NewLeadDialog";
import QuickAddDialog from "@/components/QuickAddDialog";
import BulkActionsBar from "@/components/board/BulkActionsBar";
import BulkEmailDialog from "@/components/board/BulkEmailDialog";
import PhaseColumn from "@/components/board/PhaseColumn";
import SnoozedColumn from "@/components/board/SnoozedColumn";
import { getPhases } from "@/lib/phases";
import { isSnoozed, daysBetween, priorityScore } from "@/lib/pipeline";
import { wsPipeline, isRecruiting, displayStage, ROLE_TYPES } from "@/lib/workspace";
import { exportCsv } from "@/lib/csv";
import { canWrite } from "@/lib/roles";
import { Plus, FileDown, CheckSquare, Square, Search, X, ChevronLeft, ChevronRight, Columns3, LayoutGrid, Zap } from "lucide-react";

const EMAIL_STAGES = [101, 102];

export default function BoardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [summary, setSummary] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [myCards, setMyCards] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const recruiting = isRecruiting();
  const [selectedCard, setSelectedCard] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropTargetStage, setDropTargetStage] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEmail, setBulkEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [focusedStage, setFocusedStage] = useState(null);
  const [viewMode, setViewModeState] = useState(() => localStorage.getItem("avida_board_view") || "phases");
  const setViewMode = (m) => { setViewModeState(m); localStorage.setItem("avida_board_view", m); };
  const [stageFilter, setStageFilter] = useState("");
  const [statFilter, setStatFilter] = useState("");
  const [sortMode, setSortMode] = useState("stage");
  const columnRefs = useRef({});
  const mobileColumnRefs = useRef({});
  const boardRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = boardRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollBoard = (dir) => {
    const el = boardRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  // Drag-to-pan the board (skip when starting on a card or interactive control)
  const onBoardMouseDown = (e) => {
    const el = boardRef.current;
    if (!el || e.button !== 0) return;
    if (e.target.closest('[data-testid^="kanban-card-"], button, input, select, textarea, label, a')) return;
    panRef.current = { active: true, startX: e.pageX, startLeft: el.scrollLeft, moved: false };
    el.classList.add("grabbing");
  };
  const onBoardMouseMove = (e) => {
    if (!panRef.current.active) return;
    const el = boardRef.current;
    const dx = e.pageX - panRef.current.startX;
    if (Math.abs(dx) > 3) panRef.current.moved = true;
    el.scrollLeft = panRef.current.startLeft - dx;
  };
  const endPan = () => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    boardRef.current?.classList.remove("grabbing");
  };

  const loadAll = useCallback(async () => {
    try {
      const pipeline = wsPipeline();
      const [c, t, u, l, s] = await Promise.all([
        api.get("/cards", { params: { pipeline } }),
        api.get("/templates", { params: { pipeline } }),
        api.get("/users"),
        api.get("/lists"),
        api.get("/reports/summary", { params: { pipeline } }),
      ]);
      setCards(c.data);
      setTemplates(t.data);
      setUsers(u.data);
      setLists(l.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Open a specific card when arriving via ?card=<id> (e.g. from the login briefing)
  useEffect(() => {
    const cid = new URLSearchParams(location.search).get("card");
    if (!cid || cards.length === 0) return;
    const found = cards.find((c) => c.id === cid);
    if (found) setSelectedCard(found);
    else toast.error("That lead isn't in this workspace — switch workspace to view it.");
    navigate("/", { replace: true });
  }, [cards, location.search, navigate]);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows, templates, viewMode]);

  const refreshSummary = () => api.get("/reports/summary", { params: { pipeline: wsPipeline() } }).then(({ data }) => setSummary(data));

  const refreshAfterCardChange = (updated) => {
    setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)).filter((c) => c.status === "active"));
    refreshSummary();
  };

  const finalStage = useMemo(
    () => (templates.length ? Math.max(...templates.map((t) => t.stage_number)) : null),
    [templates]
  );
  const stageTargets = useMemo(() => {
    const m = {};
    templates.forEach((t) => { m[t.stage_number] = t.day_target; });
    return m;
  }, [templates]);
  const isOverdueCard = useCallback((c) => {
    if (isSnoozed(c)) return false;
    if (finalStage && c.current_stage === finalStage) return false;
    const target = stageTargets[c.current_stage];
    if (target == null) return false;
    return daysBetween(c.stage_entered_at) > target;
  }, [finalStage, stageTargets]);

  const visibleCards = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((c) => {
      if (myCards && c.owner_id !== user?.id) return false;
      if (ownerFilter && c.owner_id !== ownerFilter) return false;
      if (roleFilter && (c.position || "") !== roleFilter) return false;
      if (statFilter === "blocked" && !c.blocked) return false;
      if (statFilter === "overdue" && !isOverdueCard(c)) return false;
      if (q) {
        const hay = [
          c.full_name,
          c.av_id,
          c.payer_mco,
          c.referral_partner_name,
          c.referral_source,
          c.marketing_effort_name,
          c.field_agent_name,
          c.note,
          ...(c.keyword_tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [cards, myCards, ownerFilter, roleFilter, statFilter, query, user, isOverdueCard]);

  const snoozedCards = useMemo(() => visibleCards.filter(isSnoozed), [visibleCards]);
  const activeCards = useMemo(() => visibleCards.filter((c) => !isSnoozed(c)), [visibleCards]);
  const roleOptions = useMemo(() => {
    const set = new Set(ROLE_TYPES);
    cards.forEach((c) => { if (c.position) set.add(c.position); });
    return [...set];
  }, [cards]);

  // When searching in the All-stages view, bring the first matching column into view
  useEffect(() => {
    if (viewMode !== "stages" || stageFilter) return;
    if (!query.trim()) return;
    let match = null;
    for (const c of activeCards) { if (match === null || c.current_stage < match) match = c.current_stage; }
    if (match === null) return;
    const id = setTimeout(() => {
      const board = boardRef.current;
      const el = columnRefs.current[match];
      if (board && el && el.offsetParent !== null) {
        const delta = (el.getBoundingClientRect().left - board.getBoundingClientRect().left) - (board.clientWidth - el.clientWidth) / 2;
        board.scrollTo({ left: board.scrollLeft + delta, behavior: "smooth" });
      }
      setFocusedStage(match);
      setTimeout(() => setFocusedStage(null), 1600);
    }, 150);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, viewMode, stageFilter, activeCards]);

  const byStage = useMemo(() => {
    const map = {};
    for (let i = 1; i <= 16; i++) map[i] = [];
    activeCards.forEach((c) => { (map[c.current_stage] = map[c.current_stage] || []).push(c); });
    if (sortMode === "priority") {
      const tmap = {};
      templates.forEach((t) => { tmap[t.stage_number] = t; });
      Object.keys(map).forEach((k) => {
        map[k].sort((a, b) => priorityScore(b, tmap[b.current_stage]) - priorityScore(a, tmap[a.current_stage]));
      });
    }
    return map;
  }, [activeCards, sortMode, templates]);

  const counts = useMemo(() => {
    const m = {};
    activeCards.forEach((c) => { m[c.current_stage] = (m[c.current_stage] || 0) + 1; });
    return m;
  }, [activeCards]);

  const stageTemplates = useMemo(
    () => (stageFilter ? templates.filter((t) => t.stage_number === Number(stageFilter)) : templates),
    [templates, stageFilter]
  );

  const scrollToStage = (stage) => {
    if (stageFilter && Number(stageFilter) !== stage) {
      setStageFilter(String(stage));
      return;
    }
    const el = [columnRefs.current[stage], mobileColumnRefs.current[stage]].find((e) => e && e.offsetParent !== null) || columnRefs.current[stage];
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setFocusedStage(stage);
    window.clearTimeout(scrollToStage._t);
    scrollToStage._t = window.setTimeout(() => setFocusedStage(null), 1600);
  };

  // --- Clickable stats strip ---
  const handleStat = (key) => {
    if (key === "overdue") { setStageFilter(""); setStatFilter((p) => (p === "overdue" ? "" : "overdue")); return; }
    if (key === "lost") { navigate("/archived"); return; }
    if (key === "active") { setStatFilter(""); setStageFilter(""); return; }
    if (key === "blocked") { setStageFilter(""); setStatFilter((p) => (p === "blocked" ? "" : "blocked")); return; }
    if (key === "services_start" && finalStage) {
      setStatFilter("");
      setViewMode("stages");
      setStageFilter((p) => (Number(p) === finalStage ? "" : String(finalStage)));
    }
  };
  const statActiveKey =
    statFilter === "blocked"
      ? "blocked"
      : statFilter === "overdue"
      ? "overdue"
      : finalStage && Number(stageFilter) === finalStage
      ? "services_start"
      : !statFilter && !stageFilter
      ? "active"
      : null;

  // --- Drag & drop ---
  const onDragStart = (e, card) => {
    if (bulkMode) return;
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, targetTemplate) => {
    if (!draggedCard) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetStage(targetTemplate.stage_number);
  };
  const onDragLeave = (e, targetTemplate) => {
    if (dropTargetStage === targetTemplate.stage_number) setDropTargetStage(null);
  };

  const dropStateFor = (stage) => {
    if (!draggedCard || dropTargetStage !== stage) return null;
    const from = draggedCard.current_stage;
    return Math.abs(stage - from) === 1 ? "valid" : "invalid";
  };

  const onDrop = async (e, targetTemplate) => {
    e.preventDefault();
    setDropTargetStage(null);
    if (!draggedCard) return;
    const from = draggedCard.current_stage;
    const to = targetTemplate.stage_number;
    const card = draggedCard;
    setDraggedCard(null);
    if (from === to) return;
    if (Math.abs(to - from) !== 1) {
      toast.error("Drag only between adjacent columns");
      return;
    }
    try {
      if (to === from - 1) {
        const { data } = await api.post(`/cards/${card.id}/move-back`);
        refreshAfterCardChange(data);
        toast.success("Moved back");
      } else if (to === from + 1) {
        const tpl = templates.find((t) => t.stage_number === from);
        const allDone = card.tasks_state.every((t) => t.done);
        if (!allDone && !tpl?.skippable) {
          toast.error(`Finish all ${card.tasks_state.length} tasks to advance.`);
          return;
        }
        const { data } = await api.post(`/cards/${card.id}/${allDone ? "advance" : "skip"}`);
        refreshAfterCardChange(data);
        toast.success(allDone ? "Advanced" : "Skipped");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // --- Selection ---
  const toggleSelect = (card) =>
    setSelectedIds((p) => {
      const n = new Set(p);
      if (n.has(card.id)) n.delete(card.id); else n.add(card.id);
      return n;
    });

  const emailStage = isRecruiting() && canWrite(user)
    ? (tpl, list) => setBulkEmail({ cards: list, preselected: [], title: `${tpl.name} · ${list.length} applicant${list.length === 1 ? "" : "s"}` })
    : undefined;
  const emailSelected = () => {
    const list = cards.filter((c) => selectedIds.has(c.id));
    setBulkEmail({ cards: list, preselected: list.map((c) => c.id), title: `${list.length} selected` });
  };

  const toggleBulkMode = () => {
    setBulkMode((p) => {
      if (p) setSelectedIds(new Set());
      return !p;
    });
  };

  const csvBoard = () => {
    const rows = visibleCards.map((c) => ({
      av_id: c.av_id,
      full_name: c.full_name,
      stage: c.current_stage,
      stage_name: templates.find((t) => t.stage_number === c.current_stage)?.name,
      payer_mco: c.payer_mco,
      owner: users.find((u) => u.id === c.owner_id)?.name || "",
      days_in_stage: Math.floor((Date.now() - new Date(c.stage_entered_at).getTime()) / 86400000),
      blocked: c.blocked,
      blocked_reason: c.blocked_reason || "",
      next_action_date: c.next_action_date || "",
      next_action_text: c.next_action_text || "",
    }));
    exportCsv(`avida-board-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const currentTemplate = selectedCard ? templates.find((t) => t.stage_number === selectedCard.current_stage) : null;

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <StatsStrip summary={summary} onSelect={handleStat} activeKey={statActiveKey} />

      <div className="px-4 sm:px-6 lg:px-8 pt-5 flex flex-wrap items-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Filters</div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#15233B]/40 pointer-events-none" />
          <input
            data-testid="board-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, AV-ID, MCO…"
            className="w-52 sm:w-64 border border-[#15233B]/15 rounded pl-7 pr-7 py-1.5 text-xs bg-white font-semibold focus:outline-none focus:ring-2 focus:ring-[#FFCB05]/50"
          />
          {query && (
            <button
              type="button"
              data-testid="board-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#15233B]/10 text-[#15233B]/50"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <select
          data-testid="owner-filter"
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white font-semibold"
        >
          <option value="">All owners</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {recruiting && (
          <select
            data-testid="role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white font-semibold"
          >
            <option value="">All roles</option>
            {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 text-xs font-semibold text-[#15233B] cursor-pointer">
          <input
            type="checkbox"
            data-testid="my-cards-toggle"
            checked={myCards}
            onChange={(e) => setMyCards(e.target.checked)}
            className="h-4 w-4 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]"
          />
          My cards
        </label>
        <div className="flex items-center rounded-md border border-[#15233B]/15 overflow-hidden" data-testid="sort-toggle">
          <button
            data-testid="sort-stage"
            onClick={() => setSortMode("stage")}
            className={`px-2.5 py-1.5 text-xs font-bold ${sortMode === "stage" ? "bg-[#15233B] text-white" : "bg-white text-[#15233B]/70 hover:bg-[#15233B]/5"}`}
          >
            Stage order
          </button>
          <button
            data-testid="sort-priority"
            onClick={() => setSortMode("priority")}
            className={`px-2.5 py-1.5 text-xs font-bold ${sortMode === "priority" ? "bg-[#15233B] text-white" : "bg-white text-[#15233B]/70 hover:bg-[#15233B]/5"}`}
          >
            Priority
          </button>
        </div>
        <div className="flex items-center rounded-md border border-[#15233B]/15 overflow-hidden">
          <button
            data-testid="board-view-phases"
            onClick={() => setViewMode("phases")}
            className={`px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 ${viewMode === "phases" ? "bg-[#15233B] text-white" : "bg-white text-[#15233B]/70 hover:bg-[#15233B]/5"}`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Simple
          </button>
          <button
            data-testid="board-view-stages"
            onClick={() => setViewMode("stages")}
            className={`px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 ${viewMode === "stages" ? "bg-[#15233B] text-white" : "bg-white text-[#15233B]/70 hover:bg-[#15233B]/5"}`}
          >
            <Columns3 className="h-3.5 w-3.5" /> All stages
          </button>
        </div>
        {viewMode === "stages" && (
          <select
            data-testid="stage-filter-select"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className={`border rounded px-2 py-1.5 text-xs font-semibold ${stageFilter ? "border-[#FBBF24] bg-[#FFF9C4]/60 text-[#15233B]" : "border-[#15233B]/15 bg-white"}`}
          >
            <option value="">All stages</option>
            {templates.map((t) => (
              <option key={t.stage_number} value={t.stage_number}>{`${displayStage(t.stage_number)} · ${t.name}`}</option>
            ))}
          </select>
        )}
        {canWrite(user) && (
          <button
            data-testid="bulk-mode-toggle"
            onClick={toggleBulkMode}
            className={`px-2.5 py-1.5 rounded text-xs font-semibold flex items-center gap-1 border ${
              bulkMode ? "bg-[#15233B] text-white border-[#15233B]" : "bg-white border-[#15233B]/15 text-[#15233B] hover:bg-[#15233B]/5"
            }`}
          >
            {bulkMode ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
            {bulkMode ? "Exit select" : "Select"}
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={csvBoard}
            data-testid="board-csv-export"
            className="px-3 py-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1"
          >
            <FileDown className="h-3.5 w-3.5" /> Export CSV
          </button>
          {canWrite(user) && (
            <button
              data-testid="quick-add-button"
              onClick={() => setShowQuickAdd(true)}
              className="hidden md:inline-flex px-3 py-2 rounded-md bg-[#15233B] hover:bg-[#1E73C9] text-white text-xs font-extrabold items-center gap-1 shadow-sm"
            >
              <Zap className="h-3.5 w-3.5 text-[#FFCB05]" /> Quick add
            </button>
          )}
          {canWrite(user) && !isRecruiting() && (
            <button
              data-testid="new-lead-button"
              onClick={() => setShowNewLead(true)}
              className="hidden md:inline-flex px-3 py-2 rounded-md bg-[#FFCB05] hover:bg-[#FBC02D] text-[#15233B] text-xs font-extrabold items-center gap-1 shadow-sm ring-1 ring-[#FFCB05]/40"
            >
              <Plus className="h-3.5 w-3.5" /> New lead
            </button>
          )}
        </div>
      </div>

      {bulkMode && selectedIds.size > 0 && (
        <BulkActionsBar
          selectedIds={Array.from(selectedIds)}
          users={users}
          lostReasons={(isRecruiting() ? lists.recruiting_lost_reasons : lists.lost_reasons) || []}
          onClose={() => { setSelectedIds(new Set()); setBulkMode(false); }}
          onDone={() => { setSelectedIds(new Set()); setBulkMode(false); loadAll(); }}
          onEmail={emailSelected}
        />
      )}
      {bulkEmail && (
        <BulkEmailDialog
          cards={bulkEmail.cards}
          preselected={bulkEmail.preselected}
          title={bulkEmail.title}
          workspace={isRecruiting() ? "recruiting" : "intake"}
          templates={templates}
          onClose={() => setBulkEmail(null)}
          onDone={loadAll}
        />
      )}

      {viewMode === "stages" && (
        <div className="md:sticky md:top-16 z-30">
          <StageSpine templates={templates} counts={counts} onPick={scrollToStage} focusedStage={focusedStage} />
        </div>
      )}

      {/* Board — simple phase view */}
      {viewMode === "phases" && (
        <div className="flex-1 px-4 sm:px-6 lg:px-8 pb-24 pt-4" data-testid="board-phase-view">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
            {getPhases(wsPipeline()).map((p) => (
              <PhaseColumn
                key={p.id}
                phase={p}
                templates={templates}
                byStage={byStage}
                users={users}
                onOpen={setSelectedCard}
                selectable={bulkMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                emailStages={EMAIL_STAGES}
                onEmailStage={emailStage}
              />
            ))}
            <SnoozedColumn
              cards={snoozedCards}
              templates={templates}
              users={users}
              onOpen={setSelectedCard}
              bodyClassName="overflow-y-auto max-h-[calc(100vh-270px)]"
            />
          </div>
        </div>
      )}

      {/* Board — detailed stage view */}
      {viewMode === "stages" && canScrollLeft && (
        <button
          type="button"
          data-testid="board-scroll-left"
          onClick={() => scrollBoard(-1)}
          aria-label="Scroll board left"
          className="hidden md:flex fixed left-3 top-1/2 -translate-y-1/2 z-30 h-11 w-11 items-center justify-center rounded-full bg-[#15233B] text-white shadow-lg ring-1 ring-white/10 hover:bg-[#1E73C9] transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {viewMode === "stages" && canScrollRight && (
        <button
          type="button"
          data-testid="board-scroll-right"
          onClick={() => scrollBoard(1)}
          aria-label="Scroll board right"
          className="hidden md:flex fixed right-3 top-1/2 -translate-y-1/2 z-30 h-11 w-11 items-center justify-center rounded-full bg-[#15233B] text-white shadow-lg ring-1 ring-white/10 hover:bg-[#1E73C9] transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      {viewMode === "stages" && (
      <div
        ref={boardRef}
        data-testid="board-scroll-container"
        onScroll={updateArrows}
        onMouseDown={onBoardMouseDown}
        onMouseMove={onBoardMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        className="flex-1 overflow-x-auto board-scroll px-4 sm:px-6 lg:px-8 pb-24 pt-4"
      >
        <div className="md:flex md:gap-4 md:items-start hidden">
          {stageTemplates.map((t) => (
            <KanbanColumn
              key={t.stage_number}
              ref={(el) => (columnRefs.current[t.stage_number] = el)}
              template={t}
              cards={byStage[t.stage_number] || []}
              users={users}
              onOpen={setSelectedCard}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              dropState={dropStateFor(t.stage_number)}
              selectable={bulkMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onHeaderClick={(tpl) => scrollToStage(tpl.stage_number)}
              isFocused={focusedStage === t.stage_number}
              onEmailStage={EMAIL_STAGES.includes(t.stage_number) ? emailStage : undefined}
            />
          ))}
          {!stageFilter && (
            <SnoozedColumn
              cards={snoozedCards}
              templates={templates}
              users={users}
              onOpen={setSelectedCard}
              className="w-[296px]"
            />
          )}
        </div>
        {/* Mobile: stack columns vertically with anchors */}
        <div className="md:hidden flex flex-col gap-5">
          {stageTemplates.map((t) => (
            <div key={t.stage_number} ref={(el) => (mobileColumnRefs.current[t.stage_number] = el)} className="scroll-mt-40">
              <KanbanColumn
                template={t}
                cards={byStage[t.stage_number] || []}
                users={users}
                onOpen={setSelectedCard}
                selectable={bulkMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onHeaderClick={(tpl) => scrollToStage(tpl.stage_number)}
                isFocused={focusedStage === t.stage_number}
                idSuffix="-mobile"
                onEmailStage={EMAIL_STAGES.includes(t.stage_number) ? emailStage : undefined}
              />
            </div>
          ))}
          {!stageFilter && (
            <SnoozedColumn
              cards={snoozedCards}
              templates={templates}
              users={users}
              onOpen={setSelectedCard}
              idSuffix="-mobile"
            />
          )}
        </div>
      </div>
      )}
      <button
        data-testid="mobile-new-lead-fab"
        onClick={() => setShowQuickAdd(true)}
        className="md:hidden fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-white shadow-lg flex items-center justify-center"
        title="Quick add lead"
      >
        <Plus className="h-6 w-6" />
      </button>

      {selectedCard && currentTemplate && (
        <CardDetailPanel
          card={selectedCard}
          template={currentTemplate}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelectedCard(null)}
          onChanged={(updated) => {
            setSelectedCard(updated.status === "archived" ? null : updated);
            refreshAfterCardChange(updated);
          }}
        />
      )}

      {showQuickAdd && (
        <QuickAddDialog
          onClose={() => setShowQuickAdd(false)}
          onCreated={(card) => {
            setCards((prev) => [...prev, card]);
            refreshSummary();
          }}
        />
      )}

      {showNewLead && (
        <NewLeadDialog
          users={users}
          lists={lists}
          templates={templates}
          onClose={() => setShowNewLead(false)}
          onCreated={(card) => {
            setCards((prev) => [...prev, card]);
            refreshSummary();
          }}
        />
      )}
      <BrandFooter />
    </div>
  );
}
